import * as Sentry from '@sentry/react'

import { ENV, IS_PROD, SENTRY_DSN } from '@constants'
import { sendAnalyticsEvents } from './common'

const EXTENSION_URL_PATTERN = /^(?:chrome|moz|safari)-extension:\/\//
// Android WebView Java bridge errors
const ANDROID_WEBVIEW_PATTERN = /^Error invoking \w+:/
// Extension-injected globals that are not defined in our code
const EXTENSION_GLOBALS_PATTERN = /\b(?:xbrowser|swbrowser|XHRInterface)\b/
// amplitude-js internal performance check fails on some Android WebViews where performance.timing is undefined
const THIRD_PARTY_SCRIPT_PATTERN = /reading 'domInteractive'/

function getExtensionId(filename: string): string {
  return filename.match(/^(?:chrome|moz|safari)-extension:\/\/([^/]+)/)?.[1] ?? 'unknown'
}

function getExternalErrorType(message: string, hasExtensionFrame: boolean): string | null {
  if (hasExtensionFrame) return 'browser_extension'
  if (ANDROID_WEBVIEW_PATTERN.test(message)) return 'android_webview'
  if (EXTENSION_GLOBALS_PATTERN.test(message)) return 'browser_extension'
  if (THIRD_PARTY_SCRIPT_PATTERN.test(message)) return 'third_party_script'
  return null
}

const WEBP_PROBE =
  'data:image/webp;base64,UklGRiQAAABXRUJQVlA4IBgAAAAwAQCdASoBAAEAAwA0JaQAA3AA/vuUAAA='

function detectWebPSupport(): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    const image = new Image()
    image.onload = () => resolve(image.width === 1)
    image.onerror = () => resolve(false)
    image.src = WEBP_PROBE
  }).catch(() => false)
}

export function setSentryAppLocale(locale: string): void {
  if (!SENTRY_DSN) return
  Sentry.setTag('app_locale', locale)
}

function setSentryClientTags(): void {
  Sentry.setTag('browser_locale', navigator.language || 'unknown')
  void detectWebPSupport().then((supported) => {
    Sentry.setTag('webp', supported ? 'true' : 'false')
  })

  const pathLocale = window.location.pathname.split('/').filter(Boolean)[0]
  if (pathLocale && /^[a-z]{2}(?:-[a-z]{2,4})?$/i.test(pathLocale)) {
    Sentry.setTag('app_locale', pathLocale.toLowerCase())
  }
}

export const initSentry = (): void => {
  if (!SENTRY_DSN) return

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: ENV,
    integrations: [
      Sentry.webVitalsIntegration({
        _experiments: {
          enableStandaloneClsSpans: true,
          enableStandaloneLcpSpans: true,
        },
      }),
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: !IS_PROD ? 1.0 : 0.2,
    replaysSessionSampleRate: 0.02,
    replaysOnErrorSampleRate: 1.0,
    sendDefaultPii: false,
    beforeSend(event, hint) {
      const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? []
      const extensionFrame = frames.find((f) => EXTENSION_URL_PATTERN.test(f.filename ?? ''))
      const error = hint.originalException
      const message = error instanceof Error ? error.message : String(error ?? '')

      const errorType = getExternalErrorType(message, !!extensionFrame)

      if (errorType) {
        sendAnalyticsEvents('external_error', {
          type: errorType,
          ...(extensionFrame && { extension_id: getExtensionId(extensionFrame.filename ?? '') }),
          message,
          page: window.location.pathname,
        }, true)
        return null
      }

      return event
    },
  })

  setSentryClientTags()
}

export { Sentry }
